import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Home, Save, Lock } from 'lucide-react';

const RESIDENCE_TYPES = [
  { value: 'INDEPENDENT', label: 'Independent Living' },
  { value: 'SIL_HOUSE', label: 'SIL House' },
  { value: 'SDA', label: 'SDA' },
  { value: 'FAMILY_HOME', label: 'Family Home' },
  { value: 'BOARDING_HOUSE', label: 'Boarding House' },
  { value: 'OTHER', label: 'Other' },
];

const EMPTY_OVERLAY = {
  residence_type_preference: '',
  living_arrangement_notes: '',
  co_resident_participant_ids: [],
  move_in_checklist_done: false,
  property_contact_name: '',
  property_contact_phone: '',
  property_manager_name: '',
  accessibility_notes: '',
};

export function AccommodationOverlayTab({ participantId }) {
  const [enabled, setEnabled] = useState(null); // null = unknown, false = disabled, true = enabled
  const [form, setForm] = useState(EMPTY_OVERLAY);
  const [exists, setExists] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const probeAndLoad = useCallback(async () => {
    setLoading(true);
    try {
      // Probe feature flag first (never 404)
      const flagRes = await api.get(`/participants/${participantId}/accommodation-overlay/feature-flag`);
      const isOn = !!flagRes.data?.enabled;
      setEnabled(isOn);
      if (!isOn) return; // fail-closed: hide form
      // Load overlay
      const r = await api.get(`/participants/${participantId}/accommodation-overlay`);
      const d = r.data?.data || EMPTY_OVERLAY;
      setForm({
        residence_type_preference: d.residence_type_preference || '',
        living_arrangement_notes: d.living_arrangement_notes || '',
        co_resident_participant_ids: d.co_resident_participant_ids || [],
        move_in_checklist_done: !!d.move_in_checklist_done,
        property_contact_name: d.property_contact_name || '',
        property_contact_phone: d.property_contact_phone || '',
        property_manager_name: d.property_manager_name || '',
        accessibility_notes: d.accessibility_notes || '',
      });
      setExists(!!r.data?.exists);
      setLastUpdated(d.last_updated_at || null);
    } catch (e) {
      // Fail-closed: any error → hide form, show disabled state
      setEnabled(false);
    } finally {
      setLoading(false);
    }
  }, [participantId]);

  useEffect(() => { probeAndLoad(); }, [probeAndLoad]);

  const save = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      const payload = {
        residence_type_preference: form.residence_type_preference || null,
        living_arrangement_notes: form.living_arrangement_notes || null,
        co_resident_participant_ids: form.co_resident_participant_ids || [],
        move_in_checklist_done: !!form.move_in_checklist_done,
        property_contact_name: form.property_contact_name || null,
        property_contact_phone: form.property_contact_phone || null,
        property_manager_name: form.property_manager_name || null,
        accessibility_notes: form.accessibility_notes || null,
      };
      const r = await api.put(`/participants/${participantId}/accommodation-overlay`, payload);
      const d = r.data?.data;
      setExists(true);
      setLastUpdated(d?.last_updated_at || null);
      toast.success('Accommodation overlay saved');
    } catch (err) {
      const msg = err?.response?.data?.detail?.message || err?.message || 'Save failed';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="text-muted-foreground py-4" data-testid="accommodation-overlay-loading">Loading accommodation overlay…</p>;
  }

  if (enabled === false) {
    return (
      <Card data-testid="accommodation-overlay-disabled">
        <CardContent className="py-6 flex items-start gap-3">
          <Lock className="h-5 w-5 text-muted-foreground mt-0.5" />
          <div className="space-y-1">
            <p className="font-medium">Accommodation overlay not enabled</p>
            <p className="text-sm text-muted-foreground">
              This optional feature is gated behind the <code className="text-xs bg-muted px-1 rounded">participant_accommodation_overlay_enabled</code> organisation flag.
              Ask an administrator to enable it in Settings → Feature Flags.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="accommodation-overlay-tab">
      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Home className="h-4 w-4" /> Living Arrangement & Property
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Participant-scoped notes and preferences. Does not replace the Houses module.
              {exists && lastUpdated && (
                <> Last updated <span className="font-mono">{new Date(lastUpdated).toLocaleString()}</span>.</>
              )}
              {!exists && <> Not yet saved — fill in fields and click <strong>Save Overlay</strong>.</>}
            </p>
          </div>
          {exists ? (
            <Badge variant="outline" data-testid="overlay-status-saved">Saved</Badge>
          ) : (
            <Badge variant="outline" data-testid="overlay-status-new">New</Badge>
          )}
        </CardHeader>
        <CardContent>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Residence Type Preference</Label>
                <Select
                  value={form.residence_type_preference || ''}
                  onValueChange={(v) => setForm({ ...form, residence_type_preference: v })}
                >
                  <SelectTrigger data-testid="overlay-residence-type"><SelectValue placeholder="Select residence type" /></SelectTrigger>
                  <SelectContent>
                    {RESIDENCE_TYPES.map(r => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-3 pt-6">
                <Switch
                  checked={form.move_in_checklist_done}
                  onCheckedChange={(v) => setForm({ ...form, move_in_checklist_done: !!v })}
                  data-testid="overlay-move-in-checklist"
                />
                <Label className="text-sm">Move-in checklist completed</Label>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Living Arrangement Notes</Label>
              <Textarea
                rows={3}
                placeholder="Preferred layout, access needs, sensory considerations…"
                value={form.living_arrangement_notes || ''}
                onChange={(e) => setForm({ ...form, living_arrangement_notes: e.target.value })}
                data-testid="overlay-living-notes"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Property Contact Name</Label>
                <Input
                  value={form.property_contact_name || ''}
                  onChange={(e) => setForm({ ...form, property_contact_name: e.target.value })}
                  placeholder="e.g., Landlord / On-site contact"
                  data-testid="overlay-contact-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Property Contact Phone</Label>
                <Input
                  value={form.property_contact_phone || ''}
                  onChange={(e) => setForm({ ...form, property_contact_phone: e.target.value })}
                  placeholder="04xx xxx xxx"
                  data-testid="overlay-contact-phone"
                />
              </div>
              <div className="space-y-2">
                <Label>Property Manager</Label>
                <Input
                  value={form.property_manager_name || ''}
                  onChange={(e) => setForm({ ...form, property_manager_name: e.target.value })}
                  placeholder="Agency / Manager"
                  data-testid="overlay-pm-name"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Accessibility Notes</Label>
              <Textarea
                rows={2}
                placeholder="Ramps, wide doors, hoist, etc."
                value={form.accessibility_notes || ''}
                onChange={(e) => setForm({ ...form, accessibility_notes: e.target.value })}
                data-testid="overlay-accessibility"
              />
            </div>

            <div className="flex justify-end">
              <Button type="submit" disabled={saving} data-testid="overlay-save-button">
                <Save className="h-4 w-4 mr-1" /> {saving ? 'Saving…' : 'Save Overlay'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default AccommodationOverlayTab;
